import {
  BRAND_BRAIN_NEXUS_COLUMNS,
  BRAND_BRAIN_NEXUS_FIELD_SCHEMAS,
  isBrandBrainNexusColumn,
  type BrandBrainNexusColumn,
  type OnboardingEditColumn,
} from "@nexus/contracts";
import {
  changedOnboardingColumns,
  onboardingDraftFromValues,
  parseOnboardingDraft,
  type OnboardingDraft,
  type OnboardingFieldError,
} from "@/features/identity-hub/lib/client-onboarding";

// ─── Brand Brain section draft ──────────────────────────────────────
// The onboarding fields reuse the Identity Hub draft rules. The Nexus-owned
// fields are kept as the exact text typed: approved claims and disclaimers
// must save with their spacing and line breaks, so they are never trimmed.

export type BrandBrainDraft = OnboardingDraft & Partial<Record<BrandBrainNexusColumn, string>>;
export type BrandBrainFieldErrors = Partial<Record<string, OnboardingFieldError>>;

export interface ParsedBrandBrainDraft {
  values: Record<string, unknown>;
  errors: BrandBrainFieldErrors;
}

export function brandBrainDraftFromValues(values: Record<string, unknown>): BrandBrainDraft {
  const draft: BrandBrainDraft = onboardingDraftFromValues(values);
  for (const column of BRAND_BRAIN_NEXUS_COLUMNS) {
    if (!(column in values)) continue;
    const value = values[column];
    draft[column] = typeof value === "string" ? value : "";
  }
  return draft;
}

export function changedBrandBrainColumns(draft: BrandBrainDraft, initial: BrandBrainDraft): string[] {
  const nexus = BRAND_BRAIN_NEXUS_COLUMNS.filter((column) => (draft[column] ?? "") !== (initial[column] ?? ""));
  return [...changedOnboardingColumns(draft, initial), ...nexus];
}

/** Request values for the changed columns. A blank Nexus field clears it; any other text is sent as typed. */
export function parseBrandBrainDraft(draft: BrandBrainDraft, columns: readonly string[]): ParsedBrandBrainDraft {
  const onboardingColumns = columns.filter((column) => !isBrandBrainNexusColumn(column)) as OnboardingEditColumn[];
  const parsed = parseOnboardingDraft(draft, onboardingColumns);
  const values: Record<string, unknown> = { ...parsed.values };
  const errors: BrandBrainFieldErrors = { ...parsed.errors };
  for (const column of columns) {
    if (!isBrandBrainNexusColumn(column)) continue;
    const result = BRAND_BRAIN_NEXUS_FIELD_SCHEMAS[column].safeParse(draft[column] ?? "");
    if (result.success) values[column] = result.data;
    else errors[column] = "invalid";
  }
  return { values, errors };
}
